import { createModule } from 'terrific-singleton';
import { TerrificSpec } from 'terrific-singleton/terrific-module';
import { removeBodyState } from '@project/shared/utils/body-state/js/body-state';
import { updateNavLevel } from '@project/shared/utils/burger/js/burger';

import getCurrentBreakpoint, { Breakpoint, onBreakpointChange } from '@project/shared/utils/breakpoints/js/breakpoints';

const classes = {
	layer: 'js-m-navigation-wrapper__layer',
	wrapper: 'js-m-navigation-wrapper__navigation-layer',
	itemLevel1: 'js-m-navigation-wrapper__item-level1',
	openLevel: 'js-m-navigation-wrapper__open-layer',
	closeLevel: 'js-m-navigation-wrapper__close-layer',
	stateOpen: 'state-m-navigation-wrapper__layer--open',
	stateItemOpen: 'state-m-navigation-wrapper__leveltrigger--is-open',
	stateItemActive: 'state-m-navigation-wrapper__leveltrigger--is-active',
	stateBurgerNav: 'state-m-burgernav--visible',
};

@createModule('NavigationWrapper')
/**
 * navigation-wrapper module implementation.
 *
 * @author Nele Rabus <nele.rabus@namics.com>
 */
export class NavigationWrapper extends TerrificSpec {
	me = this;
	$ctx: JQuery;
	start(resolve: Function) {
		this.$ctx = $(this._ctx);
		this.initNavigation();

		/**
		 * Terrific => Register & handle window resize event
		 */
		onBreakpointChange(() => this.initNavigation());

		resolve();

		$(window).on('burgerOpened', () => {
			$(window).trigger('searchFlyout:close');
			this._getOpenTrigger()
				.parent()
				.removeClass(classes.stateItemActive);
		});

	

		$(window).on('searchFlyout:open', this.closeNavigation.bind(this));
	}

	stop() {}
	_getOpenTrigger() {
		return $(this._ctx).find(`.${classes.openLevel}`);
	}
	_getCloseTrigger() {
		return $(this._ctx).find(`.${classes.closeLevel}`);
	}

	initNavigation() {
		const viewport = getCurrentBreakpoint() as string;
		if (viewport === 'small' || viewport === 'medium') {
			this.initMobileNavigation();
			return;
		} else {
			this.destroyMobileNavigation();
		}
	}

	initMobileNavigation() {
		const $ctx = $(this._ctx);
		const $open = this._getOpenTrigger();
		const $close = this._getCloseTrigger();
		const $wrapper = $(`.${classes.wrapper}`, $ctx);

		// Used namespace to avoid conflict with navigation meta bindings and possible others
		$open.on('click.MobileNavigationLink', (ev: Event) => this.onClickOpenSubnavigation(ev));
		$close.on('click.MobileNavigationLink', (ev: Event) => this.onClickCloseSubnavigation(ev));
		$ctx.on('click.MobileNavigationLink', (ev: Event) => this.closeNavigation());
		$wrapper.on('click.MobileNavigationLink', (ev: Event) => {
			ev.stopPropagation();
		});
	}

	destroyMobileNavigation() {
		const $ctx = $(this._ctx);
		const $open = this._getOpenTrigger();
		const $close = this._getCloseTrigger();
		const $wrapper = $(`.${classes.wrapper}`, $ctx);

		$open.off('click.MobileNavigationLink');
		$close.off('click.MobileNavigationLink');
		$ctx.off('click.MobileNavigationLink');
		$wrapper.off('click.MobileNavigationLink');
	}

	/**
	 * return all layers
	 */
	_getLayers() {
		return $(this._ctx).find(`.${classes.layer}`);
	}

	/**
	 * return current layer depending on the call-to-action of trigger elm.
	 * @param trigger: jquery object
	 * @param cta: 'close' / 'open', default= 'open'
	 * @return currentLayer: jquery object
	 */
	_getCurrentLayer(trigger: any, cta?: string) {
		let result = cta === 'close' ? $(trigger).closest(`.${classes.layer}`) : $(trigger).next(`.${classes.layer}`);
		// fallback => check, if no layer was found, then check if the layer is inside the trigger element
		if (result.length === 0) {
			result = $(trigger).find(`.${classes.layer}`);
		}
		return result;
	}
	_getParentsLayer(trigger: any, cta?: string) {
		const $layer = this._getCurrentLayer(trigger, cta);
		return $layer.parents(`.${classes.layer}`);
	}
	_getChildrenLayer(trigger: any, cta?: string) {
		const $layer = this._getCurrentLayer(trigger, cta);
		return $layer.next(`.${classes.layer}`);
	}

	/**
	 * get the level data of an given jquery elm 'elm'
	 * @return level: jquery object
	 */
	_getLevel(elm: JQuery): number {
		const level = $(elm).data('level');
		return typeof level !== 'number' ? parseInt(level, 10) : level;
	}

	onClickOpenSubnavigation(ev: any) {
		ev.preventDefault();
		ev.stopImmediatePropagation();
		// close all layers but parents
		this.openLayer(this._getLayers().not(this._getParentsLayer(ev.currentTarget)), false);
		// toggle active Item class
		this._getOpenTrigger()
			.parent()
			.not($(ev.currentTarget).parents())
			.removeClass(classes.stateItemActive);
		$(ev.currentTarget)
			.parent()
			.addClass(classes.stateItemActive);

		// open current layer
		this.openLayer(this._getCurrentLayer(ev.currentTarget), true);

		// update navigation level
		const nr = this._getLevel(this._getCurrentLayer(ev.currentTarget));
		updateNavLevel(nr);
	}

	onClickCloseSubnavigation(ev: Event) {
		ev.preventDefault();
		ev.stopImmediatePropagation();
		// close current layer and children
		const $currentLayer = this._getCurrentLayer(ev.currentTarget, 'close');
		$currentLayer.find(`> .${classes.openLevel}`).removeClass(classes.stateItemActive);
		this.openLayer($currentLayer.add(this._getChildrenLayer(ev.currentTarget, 'close')), false);

		// update navigation level
		const nr = this._getLevel(this._getCurrentLayer(ev.currentTarget, 'close')) - 1;
		updateNavLevel(nr);
	}

	/**
	 * Toggle Subnavigation Layer
	 */
	openLayer($layer: JQuery, show: boolean) {
		$layer.toggleClass(classes.stateOpen, show);
		if ($layer.hasClass(classes.stateOpen)) {
			$layer.find('*').each((index: number, element: HTMLElement) => {
				element.scrollTop = 0;
			});
		}
	}

	/**
	 * Close all Navigation Layers
	 */
	closeNavigation() {
		updateNavLevel();
		removeBodyState(classes.stateBurgerNav);
	}
}
